import React from 'react'
import { observer } from 'mobx-react'
import {
  SingleItemSelector,
  SelectItem,
} from 'admin/staffing/components/WorkspacePostingDialog/SingleItemSelector'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import Skill from 'iam/models/Skill'
import { AddUserToWorkspaceViewModel } from './AddUserToWorkspaceViewModel'

interface AddUserToWorkspaceDialogProps {
  vm: AddUserToWorkspaceViewModel
}

export const AddUserToWorkspaceDialog = observer(
  ({ vm }: AddUserToWorkspaceDialogProps) => {
    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>
              Add user to workspace
            </ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              Select the workspace you wish to add this user to.
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="addUserToWorkspace" onSubmit={vm.submit}>
              <FormRow>
                <FormGroupContainer
                  label="Workspace"
                  error={vm.validation.getError('workspace')}
                >
                  <SingleItemSelector
                    model={vm.workspace}
                    itemOptions={vm.availableWorkspaces}
                    itemPrefix={(item) => (
                      <Avatar
                        subject={{
                          avatar: item?.branding.logos.icon,
                        }}
                        size={24}
                      />
                    )}
                    onChange={(value) =>
                      vm.setWorkspace(value === undefined ? null : value)
                    }
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select workspace"
                    disabled={vm.submit.pending}
                    dataTest="add-user-to-workspace-workspace-selector"
                  />
                </FormGroupContainer>
              </FormRow>

              {vm.workspace && (
                <>
                  <FormRow>
                    <FormGroupContainer
                      label="Role"
                      error={vm.validation.getError('workspaceRole')}
                    >
                      <SingleItemSelector
                        model={vm.workspaceRole}
                        itemOptions={vm.workspaceRoles}
                        onChange={(value) =>
                          vm.setWorkspaceRole(
                            value === undefined ? null : value,
                          )
                        }
                        itemToString={(item) => (item ? item.name : '')}
                        placeholder="Select workspace role"
                        disabled={vm.submit.pending}
                        dataTest="add-user-to-workspace-workspace-role-selector"
                      />
                    </FormGroupContainer>
                  </FormRow>

                  <FormRow>
                    <FormGroupContainer label="Skills" sublabel="(optional)">
                      <SkillsSelector
                        vm={vm}
                        disabled={vm.submit.pending}
                        dataTest="add-user-to-workspace-skills-selector"
                      />
                    </FormGroupContainer>
                  </FormRow>

                  {vm.currentUserTeamWorkspaces.includes(vm.workspace) && (
                    <>
                      <FormRow>
                        <FormGroupContainer label="Team" sublabel="(optional)">
                          <SingleItemSelector
                            model={vm.team}
                            itemOptions={vm.teams}
                            onChange={(value) =>
                              vm.setTeam(value === undefined ? null : value)
                            }
                            itemToString={(item) => (item ? item.name : '')}
                            placeholder="Select team"
                            disabled={vm.submit.pending}
                            dataTest="add-user-to-workspace-team-selector"
                          />
                        </FormGroupContainer>
                      </FormRow>

                      {vm.team && (
                        <FormRow>
                          <FormGroupContainer
                            label="Team role"
                            error={vm.validation.getError('teamRole')}
                          >
                            <SingleItemSelector
                              model={vm.teamRole}
                              itemOptions={vm.teamRoles}
                              onChange={(value) =>
                                vm.setTeamRole(
                                  value === undefined ? null : value,
                                )
                              }
                              itemToString={(item) => (item ? item.name : '')}
                              placeholder="Select team role"
                              disabled={vm.submit.pending}
                              dataTest="add-user-to-workspace-team-role-selector"
                            />
                          </FormGroupContainer>
                        </FormRow>
                      )}
                    </>
                  )}
                </>
              )}
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Close
            </Button>
            <Button
              submit
              form="addUserToWorkspace"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="add-user-to-workspace-submit"
            >
              Add User
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)

interface SkillsSelectorProps {
  vm: AddUserToWorkspaceViewModel
  disabled?: boolean
  dataTest?: string
}

const SkillsSelector = observer(function ({
  vm,
  disabled,
  dataTest,
}: SkillsSelectorProps) {
  const itemToString = (item: Skill | undefined) => item?.name || ''

  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.availableSkills, (item, text) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.availableSkills],
  )

  const change = React.useCallback(
    (skills: Skill[]) => (skills ? vm.setSkills(skills) : undefined),
    [vm],
  )

  const itemProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...itemProps}
      itemToString={itemToString}
      value={vm.skills}
      onChange={change}
      disabled={disabled}
      multi={true}
      persist
      placeholder="Select skills"
      data-test={dataTest}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem key={item.id} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
    />
  )
})
